import { DocumentBlobBase } from "@bitory-gmbh/bsm";
import { useDocumentBlobLoadQuery } from "@/services/backend/filestorage/api/document-blob-api";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/shared/components/ui/sheet";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { DocumentReferenceNumberById } from "@/services/backend/documents/components/document-reference-number-by-id";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Spinner } from "@/shared/components/ui/spinner";

const displayTypes = ["application/pdf", "image/png", "image/jpeg"];

export function DocumentBlobSheet({ base }: { base: DocumentBlobBase }) {
  const { data, error, isLoading } = useDocumentBlobLoadQuery(base);

  if (data && !displayTypes.includes(data.contentType)) {
    return (
      <Button
        variant="link"
        onClick={downloadHandler(data.objectURL, base.filename)}
      >
        {base.filename}
      </Button>
    );
  }

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="link">{base.filename}</Button>
      </SheetTrigger>
      <SheetContent side="bottom">
        <SheetHeader>
          <SheetTitle>
            {base.filename} {base.version}
          </SheetTitle>
          <SheetDescription>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>{t(base.subdomain)}</BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>{t(base.documentKind)}</BreadcrumbItem>
                <BreadcrumbSeparator />
                <DocumentReferenceNumberById
                  serviceKind={base.subdomain.toUpperCase()}
                  documentKind={base.documentKind}
                  documentId={base.documentId}
                />
              </BreadcrumbList>
            </Breadcrumb>
          </SheetDescription>
        </SheetHeader>
        <div className="px-4 pb-4">
          {isLoading && (
            <div>
              <Spinner className="mr-2" />
              <span>{t("Lade Daten ...")}</span>
            </div>
          )}
          {error && (
            <RTKQueryErrorAlert error={error} ignoreValidationError={false} />
          )}
          {data && (
            <div className="grid grid-cols-4 gap-2">
              {/*
              iframe and not object was used, since object caused problems in Safari.
              For some reason, scrolling was not possible.
            */}
              <iframe
                title={t("Dateivorschau")}
                src={`${data.objectURL}#toolbar=0`}
                className="col-span-3 h-[80vh] w-full rounded-lg"
              >
                {t("Datei kann nicht dargestellt werden")}
              </iframe>
              <DownloadButton
                objectURL={data.objectURL}
                filename={base.filename}
              />
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DownloadButton({
  objectURL,
  filename,
}: {
  objectURL: string;
  filename: string;
}) {
  return (
    <Button variant="outline" onClick={downloadHandler(objectURL, filename)}>
      {t("Herunterladen")}
    </Button>
  );
}

function downloadHandler(objectURL: string, filename: string): () => void {
  return () => {
    const a = document.createElement("a");
    a.href = objectURL;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // The URL is not revoked: the cache entry owns it and still shows it.
  };
}
